import React, { ChangeEvent, useEffect, useState, ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import {
  FormControl,
  Typography,
  FormHelperText,
  Button,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from "@mui/material";
import GenericAlert from "../GenericAlert";
import { fetchData } from "../../utils/dataProvider";
import {
  AUTHENTICATION_PATH,
  COUNTRY_FEATURES,
  DEFAULT_COUNTRY,
  ERROR,
  ERRORS_MESSAGES,
  GET,
  IVA_SITUATIONS,
  POST,
  TAX_ID_LABELS,
} from "../../utils/constants";
import { validateInputs } from "../../utils/inputValidations";
import { ITargetValue, IValidation } from "../../interfaces/form";
import {
  IBillingFormErrors,
  IAccountInfoStep,
  IRegisterMerchantSubmit,
  IEntityCountryResponse,
  ISignupMerchant,
} from "../../interfaces/signup";
import { IAlert } from "../../interfaces/alert";
import { trimValues } from "../../utils/form";
import GenericForm from "../GenericForm";
import { countryStrategyFactory } from "../../strategies/countryStrategyFactory";


const {
  emailInvalid,
  emailRequired,
  nameRequired,
  ivaSituationRequired,
  businessNameRequired,
  cuitRequired,
} = ERRORS_MESSAGES;

const baseFormValidations: IValidation[] = [
  {
    id: "name",
    type: "required",
    message: nameRequired,
  },
  {
    id: "billingEmail",
    type: "required",
    message: emailRequired,
  },
  {
    id: "billingEmail",
    type: "email",
    message: emailInvalid,
  },
  {
    id: "businessName",
    type: "required",
    message: businessNameRequired,
  },
  {
    id: "cuit",
    type: "required",
    message: cuitRequired,
  },
];

const ivaSituationValidation: IValidation = {
  id: "ivaSituation",
  type: "required",
  message: ivaSituationRequired,
};

const getFormValidationsByCountry = (country: string): IValidation[] => {
  const strategy = countryStrategyFactory.getStrategy(country);
  const strategyValidations = strategy.getValidations();

  // Merge base validations with strategy validations
  // Strategy validations take precedence if they target the same field
  const mergedValidations = [...baseFormValidations];

  strategyValidations.forEach((strategyVal) => {
    const index = mergedValidations.findIndex(
      (v) => v.id === strategyVal.id && v.type === strategyVal.type
    );
    if (index === -1) {
      mergedValidations.push(strategyVal);
    } else {
      mergedValidations[index] = strategyVal;
    }
  });

  // Ensure CUIT/NIT validation is correct for the country
  // We remove the generic 'required' check for CUIT if the strategy provides a specific one
  // or if we want to rely solely on the strategy's 'cuit'/'nit' check which implies requirement usually,
  // but here we keep 'required' as separate check.
  // However, the baseFormValidations has a 'required' check for 'cuit'.
  // The strategy also provides 'required' check for 'cuit'.
  // So the merge above should handle it if IDs match.

  const features =
    COUNTRY_FEATURES[country] || COUNTRY_FEATURES[DEFAULT_COUNTRY];
  if (features.showIvaSituation) {
    return [...mergedValidations, ivaSituationValidation];
  }
  return mergedValidations;
};

const renderShowInputErrors = (inputErrors: string[]) =>
  inputErrors.map((message: string) => (
    <FormHelperText className="color-error" key={message}>
      {message}
    </FormHelperText>
  ));

const NameAndAliasFields = ({
  selectedValues,
  errors,
  onChange,
  showInputErrors,
}: {
  selectedValues: ISignupMerchant;
  errors: IBillingFormErrors;
  onChange: (_target: HTMLInputElement) => void;
  showInputErrors: (_inputErrors: string[]) => ReactNode;
}) => (
  <>
    <Typography className="m-t-20 f-s-14 m-b-10">Nombre y Apellido</Typography>
    <TextField
      fullWidth
      size="small"
      name="userName"
      placeholder="Ingresar nombre y apellido"
      value={selectedValues.userName}
      error={errors.userName?.length > 0}
      onChange={({ target }: ChangeEvent<HTMLInputElement>) => onChange(target)}
      autoFocus
    />
    {errors.userName?.length > 0 && showInputErrors(errors.userName)}
    <Typography className="m-t-20 f-s-14 m-b-10">Alias</Typography>
    <TextField
      fullWidth
      size="small"
      name="name"
      placeholder="Ingresar alias"
      value={selectedValues.name}
      error={errors.name?.length > 0}
      onChange={({ target }: ChangeEvent<HTMLInputElement>) => onChange(target)}
      autoFocus
    />
    {errors.name?.length > 0 && showInputErrors(errors.name)}
  </>
);

const BusinessFields = ({
  selectedValues,
  errors,
  taxIdLabel,
  onChange,
  showInputErrors,
}: {
  selectedValues: ISignupMerchant;
  errors: IBillingFormErrors;
  taxIdLabel: string;
  onChange: (_target: HTMLInputElement) => void;
  showInputErrors: (_inputErrors: string[]) => ReactNode;
}) => (
  <>
    <Typography className="m-t-20 f-s-14 m-b-10">Razón Social</Typography>
    <TextField
      fullWidth
      size="small"
      name="businessName"
      placeholder="Razón Social"
      value={selectedValues.businessName}
      error={errors.businessName?.length > 0}
      onChange={({ target }: ChangeEvent<HTMLInputElement>) => onChange(target)}
    />
    {errors.businessName?.length > 0 && showInputErrors(errors.businessName)}
    <Typography className="m-t-20 f-s-14 m-b-10">
      {taxIdLabel}
    </Typography>
    <TextField
      fullWidth
      size="small"
      name="cuit"
      placeholder={`Ingresar número de ${taxIdLabel} sin guiones ni puntos.`}
      value={selectedValues.cuit}
      error={errors.cuit?.length > 0}
      onChange={({ target }: ChangeEvent<HTMLInputElement>) => onChange(target)}
    />
    {errors.cuit?.length > 0 && showInputErrors(errors.cuit)}
  </>
);

const registerMerchant = async ({
  selectedValues,
  navigate,
  setAlert,
  token,
  setSubmitting,
}: IRegisterMerchantSubmit) => {
  setSubmitting(true);
  try {
    await fetchData({
      url: "/merchants/register",
      method: POST,
      body: { ...selectedValues, token },
    });
    navigate(`${AUTHENTICATION_PATH}?reason=signup`);
  } catch (error: unknown) {
    setAlert({ typeOfAlert: ERROR, message: String(error), showAlert: true });
  } finally {
    setSubmitting(false);
  }
};

const AccountInfoForm = (props: IAccountInfoStep) => {
  const { selectedValues, setSelectedValues, setGridSize, goBack, token } =
    props;
  const navigate = useNavigate();
  const [errors, setErrors] = useState({} as IBillingFormErrors);
  const [alert, setAlert] = useState({} as IAlert);
  const [submitting, setSubmitting] = useState(false);
  const [country, setCountry] = useState(DEFAULT_COUNTRY);

  const countryFeatures =
    COUNTRY_FEATURES[country] || COUNTRY_FEATURES[DEFAULT_COUNTRY];
  const taxIdLabel = TAX_ID_LABELS[country] || TAX_ID_LABELS[DEFAULT_COUNTRY];

  const handleChange = ({ name, value }: ITargetValue) =>
    setSelectedValues({
      ...selectedValues,
      [name]: value,
    });
  const handleSubmit = () => {
    const formValidations = getFormValidationsByCountry(country);
    const inputErrors = validateInputs(selectedValues, formValidations);
    setErrors(inputErrors);
    if (Object.keys(inputErrors).length === 0) {
      registerMerchant({
        selectedValues: trimValues(selectedValues),
        navigate,
        setAlert,
        token,
        setSubmitting,
      });
    }
  };
  const showInputErrors = (inputErrors: string[]) =>
    renderShowInputErrors(inputErrors);

  const fetchEntityCountry = async () => {
    try {
      const response: IEntityCountryResponse = await fetchData({
        url: `/entities/country/merchant/${token}`,
        method: GET,
      });
      setCountry(response.country);
    } catch (error: unknown) {
      setCountry(DEFAULT_COUNTRY);
    }
  };

  useEffect(() => {
    setGridSize(4);
    fetchEntityCountry();
  }, []);
  return (
    <>
      {alert.showAlert && <GenericAlert alert={alert} />}
      <GenericForm onSubmit={handleSubmit} disableSubmit={submitting}>
        <NameAndAliasFields
          selectedValues={selectedValues}
          errors={errors}
          onChange={handleChange}
          showInputErrors={showInputErrors}
        />
        {countryFeatures.showIvaSituation && (
          <FormControl className="m-t-20" fullWidth>
            <Typography className="f-s-14 m-b-10">
              Condición ante el IVA
            </Typography>
            <Select
              size="small"
              name="ivaSituation"
              value={selectedValues.ivaSituation}
              error={errors.ivaSituation?.length > 0}
              onChange={({ target }: SelectChangeEvent) => handleChange(target)}
            >
              {IVA_SITUATIONS.map((name: string) => (
                <MenuItem key={name} value={name}>
                  {name}
                </MenuItem>
              ))}
            </Select>
            {errors.ivaSituation?.length > 0 &&
              showInputErrors(errors.ivaSituation)}
          </FormControl>
        )}
        <BusinessFields
          selectedValues={selectedValues}
          errors={errors}
          taxIdLabel={taxIdLabel}
          onChange={handleChange}
          showInputErrors={showInputErrors}
        />
        <Typography className="m-t-20 f-s-14 m-b-10">
          Domicilio Comercial
        </Typography>
        <TextField
          fullWidth
          size="small"
          name="commercialAddress"
          placeholder="Domicilio comercial"
          value={selectedValues.commercialAddress}
          error={errors.commercialAddress?.length > 0}
          onChange={({ target }: ChangeEvent<HTMLInputElement>) =>
            handleChange(target)
          }
        />
        {errors.commercialAddress?.length > 0 &&
          showInputErrors(errors.commercialAddress)}
      </GenericForm>
      <Button
        variant="contained"
        className="m-t-40 w-100"
        disabled={submitting}
        onClick={handleSubmit}
      >
        Finalizar
      </Button>
      <Button className="m-t-20 w-100" onClick={goBack}>
        Volver
      </Button>
    </>
  );
};

export default AccountInfoForm;
